/**
 * Admin Panel — standalone page, no Layout, no user auth required.
 * Accessible at /admin
 *
 * Credentials set in backend/.env:
 *   ADMIN_USERNAME=ipltradeadmin
 *   ADMIN_PASSWORD=IPLAdmin@2026
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api') + '/admin';

const T = {
  bg:      '#0a0c10',
  surface: '#111318',
  card:    '#161b22',
  border:  'rgba(255,255,255,0.08)',
  text:    '#e6edf3',
  muted:   '#7d8590',
  accent:  '#f59e0b',
  green:   '#2ea043',
  red:     '#f85149',
  blue:    '#388bfd',
  yellow:  '#d29922',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function istMinutesToTime(mins) {
  const h = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  const ampm = h < 12 ? 'AM' : 'PM';
  const hr12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${String(hr12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${ampm} IST`;
}

function adminApi(token) {
  return axios.create({
    baseURL: API_BASE,
    headers: { Authorization: `Bearer ${token}` },
  });
}

// ── Login Screen ──────────────────────────────────────────────────────────────

function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);

  const submit = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const r = await axios.post(`${API_BASE}/login`, { username, password });
      onLogin(r.data.token, r.data.username);
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight:'100vh', background:T.bg, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'Inter',system-ui,sans-serif" }}>
      <div style={{ width:360, background:T.card, border:`1px solid ${T.border}`, borderRadius:12, padding:'36px 32px', boxShadow:'0 16px 48px rgba(0,0,0,0.5)' }}>
        <div style={{ textAlign:'center', marginBottom:32 }}>
          <div style={{ fontSize:32, marginBottom:8 }}>⚙️</div>
          <h1 style={{ margin:0, fontSize:22, fontWeight:700, color:T.text }}>Admin Panel</h1>
          <p style={{ margin:'6px 0 0', fontSize:13, color:T.muted }}>IPLTrade.in</p>
        </div>
        <form onSubmit={submit}>
          <label style={{ display:'block', marginBottom:16 }}>
            <span style={{ display:'block', fontSize:12, color:T.muted, marginBottom:6, fontWeight:500, textTransform:'uppercase', letterSpacing:'0.06em' }}>Username</span>
            <input
              value={username} onChange={e => setUsername(e.target.value)}
              autoComplete="username" required
              style={inputStyle}
            />
          </label>
          <label style={{ display:'block', marginBottom:24 }}>
            <span style={{ display:'block', fontSize:12, color:T.muted, marginBottom:6, fontWeight:500, textTransform:'uppercase', letterSpacing:'0.06em' }}>Password</span>
            <input
              type="password" value={password} onChange={e => setPassword(e.target.value)}
              autoComplete="current-password" required
              style={inputStyle}
            />
          </label>
          {error && (
            <div style={{ marginBottom:16, padding:'10px 14px', borderRadius:6, background:`${T.red}18`, border:`1px solid ${T.red}44`, color:T.red, fontSize:13 }}>
              {error}
            </div>
          )}
          <button type="submit" disabled={loading} style={{ ...btnStyle, background:T.accent, color:'#000', fontWeight:700, width:'100%' }}>
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Field renderers ───────────────────────────────────────────────────────────

function BooleanField({ fieldKey, label, value, onChange }) {
  const on = value === 'true' || value === true;
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 0', borderBottom:`1px solid ${T.border}` }}>
      <span style={{ fontSize:13, color:T.text }}>{label}</span>
      <button
        onClick={() => onChange(fieldKey, !on)}
        style={{
          width:48, height:26, borderRadius:13, border:'none', cursor:'pointer',
          background: on ? T.green : 'rgba(255,255,255,0.12)',
          position:'relative', transition:'background 0.2s', flexShrink:0,
        }}
      >
        <span style={{
          position:'absolute', top:3, left: on ? 25 : 3,
          width:20, height:20, borderRadius:'50%', background:'#fff',
          transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.4)',
        }} />
      </button>
    </div>
  );
}

function NumberField({ fieldKey, label, value, min, max, desc, onChange }) {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 0', borderBottom:`1px solid ${T.border}`, gap:12, flexWrap:'wrap' }}>
      <div>
        <div style={{ fontSize:13, color:T.text }}>{label}</div>
        {desc && <div style={{ fontSize:11, color:T.muted, marginTop:2 }}>{desc}</div>}
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
        {(fieldKey.endsWith('_IST')) && (
          <span style={{ fontSize:11, color:T.yellow }}>= {istMinutesToTime(parseInt(value)||0)}</span>
        )}
        <input
          type="number" value={value} min={min} max={max}
          onChange={e => onChange(fieldKey, e.target.value)}
          style={{ ...inputStyle, width:80, textAlign:'center' }}
        />
      </div>
    </div>
  );
}

function SelectField({ fieldKey, label, value, options, desc, onChange }) {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 0', borderBottom:`1px solid ${T.border}`, gap:12, flexWrap:'wrap' }}>
      <div>
        <div style={{ fontSize:13, color:T.text }}>{label}</div>
        {desc && <div style={{ fontSize:11, color:T.muted, marginTop:2 }}>{desc}</div>}
      </div>
      <select
        value={value} onChange={e => onChange(fieldKey, e.target.value)}
        style={{ ...inputStyle, width:'auto', paddingRight:12 }}
      >
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function PasswordField({ fieldKey, label, value, desc, onChange }) {
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(false);

  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 0', borderBottom:`1px solid ${T.border}`, gap:12, flexWrap:'wrap' }}>
      <div>
        <div style={{ fontSize:13, color:T.text }}>{label}</div>
        {desc && <div style={{ fontSize:11, color:T.muted, marginTop:2 }}>{desc}</div>}
      </div>
      <div style={{ display:'flex', gap:6, alignItems:'center', flexShrink:0 }}>
        {!editing ? (
          <>
            <span style={{ fontSize:12, color:T.muted, fontFamily:'monospace' }}>{value || '(not set)'}</span>
            <button onClick={() => { setEditing(true); onChange(fieldKey, ''); }} style={smallBtn}>Change</button>
          </>
        ) : (
          <>
            <input
              type={show ? 'text' : 'password'}
              placeholder="New value…"
              onChange={e => onChange(fieldKey, e.target.value)}
              style={{ ...inputStyle, width:200 }}
              autoFocus
            />
            <button onClick={() => setShow(s => !s)} style={smallBtn}>{show ? 'Hide' : 'Show'}</button>
            <button onClick={() => setEditing(false)} style={{ ...smallBtn, color:T.muted }}>Cancel</button>
          </>
        )}
      </div>
    </div>
  );
}

function TextField({ fieldKey, label, value, desc, onChange }) {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 0', borderBottom:`1px solid ${T.border}`, gap:12, flexWrap:'wrap' }}>
      <div>
        <div style={{ fontSize:13, color:T.text }}>{label}</div>
        {desc && <div style={{ fontSize:11, color:T.muted, marginTop:2 }}>{desc}</div>}
      </div>
      <input
        type="text" value={value}
        onChange={e => onChange(fieldKey, e.target.value)}
        style={{ ...inputStyle, width:180 }}
      />
    </div>
  );
}

// ── Config Group Card ─────────────────────────────────────────────────────────

function ConfigGroup({ group, values, onChange, onSave, saving, saved }) {
  return (
    <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:8, marginBottom:20, overflow:'hidden' }}>
      <div style={{ padding:'14px 20px', borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <h3 style={{ margin:0, fontSize:15, fontWeight:600, color:T.text }}>{group.group}</h3>
            {group.restartRequired && (
              <span style={{ fontSize:10, padding:'2px 8px', borderRadius:4, background:`${T.yellow}20`, color:T.yellow, border:`1px solid ${T.yellow}40`, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em' }}>
                ⟳ Restart required
              </span>
            )}
          </div>
          {group.desc && <p style={{ margin:'4px 0 0', fontSize:12, color:T.muted }}>{group.desc}</p>}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          {saved && <span style={{ fontSize:12, color:T.green }}>✓ Saved</span>}
          <button onClick={() => onSave(group)} disabled={saving} style={{ ...btnStyle, background:T.blue, color:'#fff', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
      <div style={{ padding:'4px 20px 12px' }}>
        {group.keys.map(k => {
          const val = values[k.key] ?? '';
          if (k.type === 'boolean') return <BooleanField key={k.key} fieldKey={k.key} label={k.label} value={val} onChange={onChange} />;
          if (k.type === 'number')  return <NumberField  key={k.key} fieldKey={k.key} label={k.label} value={val} min={k.min} max={k.max} desc={k.desc} onChange={onChange} />;
          if (k.type === 'select')  return <SelectField  key={k.key} fieldKey={k.key} label={k.label} value={val} options={k.options} desc={k.desc} onChange={onChange} />;
          if (k.type === 'password')return <PasswordField key={k.key} fieldKey={k.key} label={k.label} value={val} desc={k.desc} onChange={onChange} />;
          return <TextField key={k.key} fieldKey={k.key} label={k.label} value={val} desc={k.desc} onChange={onChange} />;
        })}
      </div>
    </div>
  );
}

// ── Main Admin Panel ──────────────────────────────────────────────────────────

function SessionResetPanel({ api }) {
  const [sessionYear,  setSessionYear]  = useState(new Date().getFullYear());
  const [resetting,    setResetting]    = useState(false);
  const [resetResult,  setResetResult]  = useState(null);
  const [resetError,   setResetError]   = useState('');
  const [confirmStep,  setConfirmStep]  = useState(false);

  const doReset = async () => {
    setResetting(true);
    setResetError('');
    setResetResult(null);
    try {
      const r = await api.post('/reset-session', { session_year: sessionYear });
      setResetResult(r.data);
      setConfirmStep(false);
    } catch (err) {
      setResetError(err.response?.data?.error || 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div style={{ background:T.card, border:`1px solid ${T.red}40`, borderLeft:`4px solid ${T.red}`, borderRadius:8, marginBottom:24, overflow:'hidden' }}>
      <div style={{ padding:'14px 20px', borderBottom:`1px solid ${T.border}` }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
          <h3 style={{ margin:0, fontSize:15, fontWeight:600, color:T.text }}>🔄 New Season Reset</h3>
          <span style={{ fontSize:10, padding:'2px 8px', borderRadius:4, background:`${T.red}20`, color:T.red, border:`1px solid ${T.red}40`, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em' }}>Destructive</span>
        </div>
        <p style={{ margin:0, fontSize:12, color:T.muted }}>
          Snapshots the current leaderboard, then resets all users' balances to 1,000 pts, clears portfolios, and resets trade counts.
          Previous season data is preserved in leaderboard history. <strong style={{ color:T.red }}>This cannot be undone.</strong>
        </p>
      </div>
      <div style={{ padding:'14px 20px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
          <div>
            <label style={{ display:'block', fontSize:11, color:T.muted, marginBottom:4, textTransform:'uppercase', letterSpacing:'0.06em' }}>Session Year to snapshot</label>
            <input
              type="number" value={sessionYear} min={2020} max={2100}
              onChange={e => setSessionYear(parseInt(e.target.value))}
              style={{ ...inputStyle, width:90, textAlign:'center' }}
            />
          </div>
          {!confirmStep ? (
            <button
              onClick={() => setConfirmStep(true)}
              style={{ ...btnStyle, background:'transparent', border:`1px solid ${T.red}`, color:T.red, marginTop:18 }}
            >
              Reset New Season
            </button>
          ) : (
            <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:18 }}>
              <span style={{ fontSize:12, color:T.red, fontWeight:600 }}>⚠️ Are you sure? This will wipe all portfolios!</span>
              <button onClick={doReset} disabled={resetting} style={{ ...btnStyle, background:T.red, color:'#fff', opacity: resetting ? 0.7 : 1 }}>
                {resetting ? 'Resetting…' : 'Yes, Reset Now'}
              </button>
              <button onClick={() => setConfirmStep(false)} style={{ ...btnStyle, background:'transparent', border:`1px solid ${T.border}`, color:T.muted }}>
                Cancel
              </button>
            </div>
          )}
        </div>
        {resetResult && (
          <div style={{ marginTop:12, padding:'10px 14px', borderRadius:6, background:`${T.green}14`, border:`1px solid ${T.green}40`, fontSize:12, color:T.green }}>
            ✓ {resetResult.message} ({resetResult.snapshotted} users snapshotted)
          </div>
        )}
        {resetError && (
          <div style={{ marginTop:12, padding:'10px 14px', borderRadius:6, background:`${T.red}14`, border:`1px solid ${T.red}40`, fontSize:12, color:T.red }}>
            {resetError}
          </div>
        )}
      </div>
    </div>
  );
}

function AdminPanel({ token, username, onLogout }) {
  const [groups,       setGroups]      = useState([]);
  const [values,       setValues]      = useState({});
  const [loadError,    setLoadError]   = useState('');
  const [savingGroup,  setSavingGroup] = useState(null);
  const [savedGroups,  setSavedGroups] = useState({});
  const [saveError,    setSaveError]   = useState('');
  const api = adminApi(token);

  const load = useCallback(async () => {
    setLoadError('');
    try {
      const r = await api.get('/config');
      setGroups(r.data.groups);
      const flat = {};
      r.data.groups.forEach(g => g.keys.forEach(k => { flat[k.key] = k.value; }));
      setValues(flat);
    } catch (err) {
      setLoadError(err.response?.data?.error || 'Failed to load config');
      if (err.response?.status === 401) onLogout();
    }
  }, [token]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const handleChange = useCallback((key, val) => {
    setValues(prev => ({ ...prev, [key]: val }));
  }, []);

  const handleSave = useCallback(async group => {
    setSaveError('');
    setSavingGroup(group.group);
    const updates = {};
    group.keys.forEach(k => { updates[k.key] = values[k.key] ?? ''; });
    try {
      await api.put('/config', { updates });
      setSavedGroups(prev => ({ ...prev, [group.group]: true }));
      setTimeout(() => setSavedGroups(prev => { const n={...prev}; delete n[group.group]; return n; }), 3000);
      // Reload to get fresh masked values for password fields
      await load();
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSavingGroup(null);
    }
  }, [values, api, load]);

  return (
    <div style={{ minHeight:'100vh', background:T.bg, fontFamily:"'Inter',system-ui,sans-serif", color:T.text }}>
      {/* Top bar */}
      <div style={{ background:T.surface, borderBottom:`1px solid ${T.border}`, padding:'12px 24px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ fontSize:20 }}>⚙️</span>
          <div>
            <span style={{ fontWeight:700, fontSize:16, color:T.text }}>Admin Panel</span>
            <span style={{ marginLeft:10, fontSize:12, color:T.muted }}>IPLTrade.in</span>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:14 }}>
          <span style={{ fontSize:13, color:T.muted }}>Signed in as <strong style={{ color:T.text }}>{username}</strong></span>
          <button onClick={onLogout} style={{ ...btnStyle, background:'transparent', border:`1px solid ${T.border}`, color:T.muted }}>
            Sign Out
          </button>
        </div>
      </div>

      <div style={{ maxWidth:860, margin:'0 auto', padding:'28px 24px' }}>
        <div style={{ marginBottom:24 }}>
          <h2 style={{ margin:0, fontSize:20, fontWeight:700 }}>Environment Settings</h2>
          <p style={{ margin:'6px 0 0', fontSize:13, color:T.muted }}>
            Changes are written to <code style={{ background:'rgba(255,255,255,0.06)', padding:'1px 6px', borderRadius:4 }}>.env</code> and applied to the running process immediately.
            Settings marked <span style={{ color:T.yellow }}>⟳ Restart required</span> take full effect after restarting the server.
          </p>
        </div>

        {loadError && (
          <div style={{ padding:'12px 16px', borderRadius:8, background:`${T.red}18`, border:`1px solid ${T.red}40`, color:T.red, marginBottom:20, fontSize:13 }}>
            {loadError}
          </div>
        )}
        {saveError && (
          <div style={{ padding:'12px 16px', borderRadius:8, background:`${T.red}18`, border:`1px solid ${T.red}40`, color:T.red, marginBottom:20, fontSize:13 }}>
            {saveError}
          </div>
        )}

        {groups.length === 0 && !loadError && (
          <div style={{ textAlign:'center', padding:60, color:T.muted }}>Loading…</div>
        )}

        <SessionResetPanel api={api} />

        {groups.map(g => (
          <ConfigGroup
            key={g.group}
            group={g}
            values={values}
            onChange={handleChange}
            onSave={handleSave}
            saving={savingGroup === g.group}
            saved={!!savedGroups[g.group]}
          />
        ))}
      </div>
    </div>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'ipl_admin_token';

export default function AdminPage() {
  const [token,    setToken]    = useState(() => sessionStorage.getItem(STORAGE_KEY) || '');
  const [username, setUsername] = useState('');

  const handleLogin = (tok, user) => {
    setToken(tok);
    setUsername(user);
    sessionStorage.setItem(STORAGE_KEY, tok);
  };

  const handleLogout = () => {
    setToken('');
    setUsername('');
    sessionStorage.removeItem(STORAGE_KEY);
  };

  if (!token) return <LoginScreen onLogin={handleLogin} />;
  return <AdminPanel token={token} username={username} onLogout={handleLogout} />;
}

// ── Shared styles ─────────────────────────────────────────────────────────────

const inputStyle = {
  background: '#0d1117', border: `1px solid rgba(255,255,255,0.12)`, borderRadius: 6,
  color: T.text, padding: '8px 12px', fontSize: 13, outline: 'none', width: '100%',
  boxSizing: 'border-box',
};

const btnStyle = {
  padding: '7px 16px', borderRadius: 6, border: 'none', cursor: 'pointer',
  fontSize: 13, fontWeight: 600, transition: 'opacity 0.15s',
};

const smallBtn = {
  padding: '4px 10px', borderRadius: 4, border: `1px solid ${T.border}`, cursor: 'pointer',
  fontSize: 11, fontWeight: 600, background: 'rgba(255,255,255,0.06)', color: T.text,
};
